import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, Flex, Typography, Select, Button, Popconfirm, Alert, Avatar, App, theme } from 'antd';
import { BankOutlined, DeleteOutlined, PlusOutlined, PartitionOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useRemoveAnyCompanyMemberMutation,
  useRemoveClientMemberMutation,
  useUpdateAnyCompanyMemberRoleMutation,
  useUpdateTenantRoleMutation
} from '@/api/auth/mutations';
import type { Company, UserTenantCompanyMembership } from '@/api/auth/interfaces';
import { COMPANY_ROLES, TENANT_ROLES, TENANT_ROLE_ORDER, isTenantRole, type TenantRoleType } from '@/constants/tenantRoles';
import { useTenantRoleOptions } from '@/hooks/useTenantRoleOptions';
import { TenantRoleEditor } from '@/components/tenant/TenantRoleEditor';
import { getApiErrorMessage } from '@/utils/apiError';
import { AddCompanyMemberModal } from './AddCompanyMemberModal';
import { TenantRoleTag } from './TenantRoleTag';

const { Text } = Typography;

export interface UserTenantAccessCardProps {
  userId: string;
  userName: string;
  clientId: string;
  clientName: string;
  /** The user's tenant role; null when they only have company memberships under this tenant (inconsistent data) */
  tenantRole: TenantRoleType | null;
  /** The user's active company memberships under this tenant */
  companies: UserTenantCompanyMembership[];
  /** Every company of the tenant (for "Add to company") */
  tenantCompanies: Company[];
  /** Shows the role editors and add / remove actions */
  canManage: boolean;
}

/**
 * One tenant a user has access to, on the admin user page: tenant role, the companies they
 * belong to in it (with their company role), and the actions to change or remove each of them.
 */
export function UserTenantAccessCard({
  userId,
  userName,
  clientId,
  clientName,
  tenantRole,
  companies,
  tenantCompanies,
  canManage
}: UserTenantAccessCardProps) {
  const { t } = useTranslation('admin');
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const [addToCompanyOpen, setAddToCompanyOpen] = useState(false);
  const companyRoleOptions = useTenantRoleOptions(COMPANY_ROLES);

  const updateTenantRole = useUpdateTenantRoleMutation(clientId);
  const removeFromTenant = useRemoveClientMemberMutation(clientId);
  const updateCompanyRole = useUpdateAnyCompanyMemberRoleMutation(clientId);
  const removeFromCompany = useRemoveAnyCompanyMemberMutation(clientId);

  const memberCompanyIds = new Set(companies.map((company) => company.companyId));
  const addableCompanies = tenantCompanies.filter((company) => !memberCompanyIds.has(company.id));
  // Customers belong to the tenant only; company membership is for staff
  const canHaveCompanies = tenantRole !== TENANT_ROLES.CUSTOMER;

  /** Shows a translated error toast for a failed membership request. */
  const reportError = (error: unknown) => message.error(getApiErrorMessage(error, t('users.detail.tenantAccess.actionError')));

  const handleRemoveFromTenant = () =>
    removeFromTenant.mutate(userId, {
      onSuccess: () => message.success(t('users.detail.tenantAccess.removedFromTenant', { tenant: clientName })),
      onError: reportError
    });

  const handleAddTenantLink = () =>
    updateTenantRole.mutate(
      { userId, tenantRole: TENANT_ROLES.EMPLOYEE },
      { onSuccess: () => message.success(t('users.detail.tenantAccess.tenantLinkAdded')), onError: reportError }
    );

  const handleCompanyRoleChange = (companyId: string, role: TenantRoleType) =>
    updateCompanyRole.mutate(
      { companyId, userId, tenantRole: role },
      { onSuccess: () => message.success(t('users.detail.tenantAccess.companyRoleUpdated')), onError: reportError }
    );

  const handleRemoveFromCompany = (companyId: string) =>
    removeFromCompany.mutate(
      { companyId, userId },
      { onSuccess: () => message.success(t('users.detail.tenantAccess.removedFromCompany')), onError: reportError }
    );

  return (
    <Card
      size="small"
      styles={{ header: { padding: `${token.paddingSM}px ${token.padding}px` }, body: { padding: token.padding } }}
      title={
        <Flex align="center" gap={token.marginSM}>
          <Avatar shape="square" size={32} icon={<PartitionOutlined />} style={{ backgroundColor: token.colorPrimaryBg, color: token.colorPrimary }} />
          <Link to={`/admin/tenants/${clientId}`} style={{ fontWeight: 600, fontSize: 15 }}>
            {clientName}
          </Link>
        </Flex>
      }
      extra={
        tenantRole && (
          <Flex align="center" gap={token.marginSM}>
            {canManage ? (
              <TenantRoleEditor clientId={clientId} userId={userId} currentRole={tenantRole} userName={userName} allowedRoles={TENANT_ROLE_ORDER} />
            ) : (
              <TenantRoleTag role={tenantRole} />
            )}
            {canManage && (
              <Popconfirm
                title={t('users.detail.tenantAccess.removeConfirmTitle', { tenant: clientName })}
                description={t('users.detail.tenantAccess.removeConfirmDesc')}
                okText={t('tenants.detail.removeFromTenant.ok')}
                cancelText={t('tenants.detail.removeFromTenant.cancel')}
                okButtonProps={{ danger: true }}
                onConfirm={handleRemoveFromTenant}
              >
                <Button type="text" danger size="small" icon={<DeleteOutlined />} loading={removeFromTenant.isPending}>
                  {t('tenants.detail.removeFromTenant.button')}
                </Button>
              </Popconfirm>
            )}
          </Flex>
        )
      }
    >
      {!tenantRole && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: token.marginSM }}
          title={t('users.detail.tenantAccess.missingTenantLink')}
          action={
            canManage && (
              <Button size="small" onClick={handleAddTenantLink} loading={updateTenantRole.isPending}>
                {t('users.detail.tenantAccess.addTenantLink')}
              </Button>
            )
          }
        />
      )}

      <Flex vertical gap={token.marginXS}>
        {companies.map((company) => {
          const companyRole = company.roles[0];
          return (
            <Flex
              key={company.companyId}
              align="center"
              justify="space-between"
              gap={token.marginSM}
              style={{ padding: `${token.paddingXS}px ${token.paddingSM}px`, background: token.colorFillQuaternary, borderRadius: token.borderRadius }}
            >
              <Flex align="center" gap={token.marginXS} style={{ minWidth: 0 }}>
                <BankOutlined style={{ color: token.colorTextSecondary }} />
                <Text ellipsis>{company.companyName}</Text>
              </Flex>
              <Flex align="center" gap={token.marginXS}>
                {canManage && isTenantRole(companyRole) ? (
                  <Select
                    size="small"
                    value={companyRole}
                    style={{ width: 130 }}
                    options={companyRoleOptions.map(({ value, label }) => ({ value, label }))}
                    onChange={(role: TenantRoleType) => handleCompanyRoleChange(company.companyId, role)}
                  />
                ) : (
                  <TenantRoleTag role={companyRole} />
                )}
                {canManage && (
                  <Popconfirm
                    title={t('tenants.detail.removeFromCompany.confirmTitle')}
                    description={t('tenants.detail.removeFromCompany.confirmDesc')}
                    okText={t('tenants.detail.removeFromCompany.ok')}
                    cancelText={t('tenants.detail.removeFromCompany.cancel')}
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleRemoveFromCompany(company.companyId)}
                  >
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} aria-label={t('tenants.detail.removeFromCompany.ok')} />
                  </Popconfirm>
                )}
              </Flex>
            </Flex>
          );
        })}

        {companies.length === 0 && (
          <Text type="secondary">
            {canHaveCompanies ? t('users.detail.tenantAccess.noCompanies') : t('users.detail.tenantAccess.customerNoCompanies')}
          </Text>
        )}

        {canManage && canHaveCompanies && tenantRole && addableCompanies.length > 0 && (
          <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => setAddToCompanyOpen(true)} style={{ alignSelf: 'flex-start' }}>
            {t('tenants.detail.addToCompany.short')}
          </Button>
        )}
      </Flex>

      {addToCompanyOpen && (
        <AddCompanyMemberModal
          open={addToCompanyOpen}
          onCancel={() => setAddToCompanyOpen(false)}
          clientId={clientId}
          clientMembers={[]}
          companies={addableCompanies}
          initialUserId={userId}
        />
      )}
    </Card>
  );
}
